import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  Container, ThemeProvider, Typography,
} from '@material-ui/core';
import { usePostHog } from 'posthog-js/react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';

import {
  ContentContainer,
  HeaderBox,
  Title,
  ConsentRow,
  ConsentCheckbox,
  ConsentText,
  ConsentLink,
  SubmitButtonBox,
} from './UserInfoConfirmation.styles';

import sparkTheme from '@/themes/sparkTheme';
import Button from '@/components/Button';
import { TextField } from '@/components';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import {
  Product, getProductMinAge, getProductPrivacyPolicy, getProductTermsAndConditions,
} from '@/utils/productHelpers';
import { getProgram } from '@/utils/posthog';
import { EDIT_PATIENT_INFO } from '@/apollo/mutations';
import { getSupabaseClient } from '@/utils/supabaseClient';
import useBlockBackNavigation from '@/hooks/useBlockBackNavigation';
import { MINIMUM_BIRTH_YEAR } from '@/utils/constants';
import { getAge } from '@/utils/stringValidators';
import { parseDate, formatDateString } from '@/utils/stringUtils';
import { formatDateInput } from '@/utils/inputHelpers';

const EDIT_PATIENT_CHANGE_REASON = 'updating patient info in cortex from spark in app activation flow';

interface UserInfoLocationState {
  accessCode?: string;
  phoneNumber?: string;
  patientUid?: string;
}

type SchemaErrorMessages = {
  firstName: string;
  lastName: string;
  dob: string;
  dobMinAge: string;
};

const createSchema = (errorMessages: SchemaErrorMessages, minAge: number) => z.object({
  firstName: z.string({ required_error: errorMessages.firstName })
    .min(1, { message: errorMessages.firstName }),
  lastName: z.string({ required_error: errorMessages.lastName })
    .min(1, { message: errorMessages.lastName }),
  dob: z.string({ required_error: errorMessages.dob })
    .length(14, { message: errorMessages.dob })
    .refine(
      (dob) => {
        const parsedDate = parseDate(dob);
        const date = new Date(parsedDate);
        const isValidDate = !Number.isNaN(date.getTime()) && date.getFullYear() >= MINIMUM_BIRTH_YEAR;
        return isValidDate && getAge(dob) >= minAge;
      },
      (dob) => {
        const parsedDate = parseDate(dob);
        const date = new Date(parsedDate);
        const isValidDate = !Number.isNaN(date.getTime()) && date.getFullYear() >= MINIMUM_BIRTH_YEAR;
        return { message: isValidDate ? errorMessages.dobMinAge : errorMessages.dob };
      },
    ),
});

type UserInfoForm = z.infer<ReturnType<typeof createSchema>>;

const UserInfoConfirmationInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();
  useBlockBackNavigation();

  const { t } = useTranslation();
  const posthog = usePostHog();
  const { product } = useParams<{ product: Product }>();
  const { state: prefillData } = useLocation<UserInfoLocationState>();
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [editPatientInfo, { loading: isSubmitting }] = useMutation(EDIT_PATIENT_INFO);

  const program = getProgram(product);
  const minAge = getProductMinAge(product);

  const schema = createSchema({
    firstName: t('coverage.errors.first_name_error'),
    lastName: t('coverage.errors.last_name_error'),
    dob: t('coverage.errors.dob_error'),
    dobMinAge: t('coverage.pre_onboarding.user_info_confirmation.dob_error_min_age', { minAge }),
  }, minAge);

  const {
    control,
    handleSubmit,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<UserInfoForm>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      dob: '',
    },
  });

  useEffect(() => {
    posthog?.capture('SparkUserInfoConfirmationScreenViewed', { program });
  }, [posthog, program]);

  const onSubmit = async (data: UserInfoForm) => {
    setSubmitError('');

    try {
      // Retrieve the Supabase session set during OTP verification.
      // Cortex auth_utils.py validates this JWT and attaches jwt_context to the request.
      const { data: { session } } = await getSupabaseClient().auth.getSession();

      const variables = {
        patientUid: prefillData?.patientUid ?? '',
        changeReason: EDIT_PATIENT_CHANGE_REASON,
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: formatDateString(data.dob),
        phone: prefillData?.phoneNumber ?? null,
      };

      const response = await editPatientInfo({
        variables,
        context: {
          headers: {
            Authorization: session?.access_token ? `Bearer ${session.access_token}` : '',
          },
        },
      });
      const result = response.data?.editPatientInfo;

      if (result?.success) {
        posthog?.capture('SparkUserInfoConfirmationSubmitted', { program });
        // TODO: SD-1960 — initiate session handoff to native Spark app (authorize + deep link redirect).
        return;
      }

      posthog?.capture('SparkUserInfoConfirmationErrored', { program });
      setSubmitError(t('coverage.errors.generic_error'));
    } catch {
      posthog?.capture('SparkUserInfoConfirmationErrored', { program });
      setSubmitError(t('coverage.errors.generic_error'));
    }
  };

  const isSubmitEnabled = isValid && consentChecked && !isSubmitting;
  const privacyPolicyUrl = getProductPrivacyPolicy(product);
  const termsUrl = getProductTermsAndConditions(product);

  return (
    <ContentContainer>
      <Header />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <HeaderBox>
          <Title variant="h2" color="textPrimary">
            {t('coverage.pre_onboarding.user_info_confirmation.title')}
          </Title>
          <Typography color="textPrimary">
            {t('coverage.pre_onboarding.user_info_confirmation.sub_title')}
          </Typography>
        </HeaderBox>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* First Name */}
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                required
                label={t('coverage.spark.patient.f_name_label')}
                placeholder={t('coverage.pre_onboarding.user_info_confirmation.first_name_placeholder')}
                helperText={errors.firstName?.message}
                error={!!errors.firstName}
                onFocus={() => clearErrors('firstName')}
                inputProps={{ 'data-testid': 'first-name-input' }}
              />
            )}
          />

          {/* Last Name */}
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                required
                label={t('coverage.spark.patient.l_name_label')}
                placeholder={t('coverage.pre_onboarding.user_info_confirmation.last_name_placeholder')}
                helperText={errors.lastName?.message}
                error={!!errors.lastName}
                onFocus={() => clearErrors('lastName')}
                inputProps={{ 'data-testid': 'last-name-input' }}
              />
            )}
          />

          {/* Date of Birth */}
          <Controller
            name="dob"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                required
                label={t('coverage.spark.patient.dob_label')}
                placeholder={t('coverage.pre_onboarding.user_info_confirmation.dob_placeholder')}
                helperText={errors.dob?.message}
                error={!!errors.dob}
                onFocus={() => clearErrors('dob')}
                inputProps={{
                  'data-testid': 'dob-input',
                  maxLength: 14,
                  inputMode: 'numeric',
                }}
                FormHelperTextProps={
                  { 'data-testid': 'dob-error' } as React.HTMLAttributes<HTMLParagraphElement>
                }
                onChange={(e) => {
                  const value = formatDateInput(e.target.value);
                  field.onChange(value);
                }}
              />
            )}
          />

          {/* Privacy Policy + Terms of Use consent */}
          <ConsentRow>
            <ConsentCheckbox
              type="checkbox"
              data-testid="consent-checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              aria-label={t('coverage.pre_onboarding.user_info_confirmation.consent_aria_label')}
            />
            <ConsentText>
              {t('coverage.pre_onboarding.user_info_confirmation.consent_prefix')}
              {' '}
              <ConsentLink
                href={privacyPolicyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="privacy-policy-link"
              >
                {t('coverage.privacy_policy')}
              </ConsentLink>
              {' '}
              {t('coverage.and')}
              {' '}
              <ConsentLink
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="terms-link"
              >
                {t('coverage.terms_of_use')}
              </ConsentLink>
              {t('coverage.pre_onboarding.user_info_confirmation.consent_suffix')}
            </ConsentText>
          </ConsentRow>

          <SubmitButtonBox>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              rounded
              type="submit"
              data-testid="submit-button"
              disabled={!isSubmitEnabled}
            >
              {isSubmitting
                ? t('coverage.pre_onboarding.user_info_confirmation.submitting')
                : t('coverage.submit')}
            </Button>
          </SubmitButtonBox>

          {submitError && (
            <Typography color="error" data-testid="submit-error" style={{ marginTop: 8 }}>
              {submitError}
            </Typography>
          )}
        </form>

        <ConsentWidget />
      </Container>
      <Footer />
    </ContentContainer>
  );
};

const UserInfoConfirmation: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <UserInfoConfirmationInner />
  </ThemeProvider>
);

export default UserInfoConfirmation;
